'use client';

import { useAuth } from '@/contexts/AuthContext';
import { useUserUsage } from '@/hooks/useUserUsage';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { LoaderIcon } from '@/components/icons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

export function SidebarUserLink() {
  const { user: firebaseUser, loading: firebaseLoading } = useAuth();
  const { data: usageData } = useUserUsage();
  const pathname = usePathname();
  const isActive = pathname === '/profile';
  const { setOpenMobile } = useSidebar();

  const getUserInitials = (name: string) =>
    name.split(' ').map((w) => w.charAt(0)).join('').toUpperCase().slice(0, 2);

  const user = {
    name: firebaseUser?.displayName || 'User',
    email: firebaseUser?.email || '',
    avatar: firebaseUser?.photoURL || `https://avatar.vercel.sh/${firebaseUser?.email}`,
  };

  return (
    <SidebarMenu data-tour="sidebar-profile-section">
      <SidebarMenuItem>
        {firebaseLoading ? (
          <SidebarMenuButton className="h-10 justify-between">
            <div className="flex flex-row gap-2">
              <div className="size-6 bg-zinc-500/30 rounded-full animate-pulse" />
              <span className="bg-zinc-500/30 text-transparent rounded-md animate-pulse">
                Loading auth status
              </span>
            </div>
            <div className="animate-spin text-zinc-500">
              <LoaderIcon />
            </div>
          </SidebarMenuButton>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <SidebarMenuButton
                size="lg"
                asChild
                isActive={isActive}
                className={
                  usageData?.is_enterprise
                    ? 'ring-1 ring-amber-500/50 shadow-[0_0_8px_rgba(245,158,11,0.15)]'
                    : ''
                }
              >
                <Link href="/profile" onClick={() => setOpenMobile(false)}>
                  <Avatar
                    className={`size-8 rounded-lg ${
                      usageData?.is_enterprise ? 'ring-2 ring-amber-500/60' : ''
                    }`}
                  >
                    <AvatarImage src={user.avatar} alt={user.name} />
                    <AvatarFallback className="rounded-lg">
                      {getUserInitials(user.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">{user.name}</span>
                    <span className="truncate text-xs">{user.email}</span>
                    {usageData?.is_enterprise && (
                      <span className="text-[10px] font-medium text-amber-500">Enterprise</span>
                    )}
                  </div>
                </Link>
              </SidebarMenuButton>
            </TooltipTrigger>
            <TooltipContent side="right" className="text-[12px]">
              Profile, usage &amp; logs
            </TooltipContent>
          </Tooltip>
        )}
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
